import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useApiClient } from "@/lib/api";
import type {
  IntegrationCheckResult,
  MigrateProjectPayload,
  MigrationTool,
} from "@/lib/migration";

/**
 * POST /api/projects/migrate takes longer than an ordinary write: it re-verifies every linked tool
 * with its provider, runs one transaction, then spends up to 6s adding the team to the linked
 * tools. The shared client's 10s timeout would abort a request that is about to succeed, so this
 * call uses API Gateway's 29s ceiling minus a margin. Same reasoning for the per-field validate.
 */
export const MIGRATE_TIMEOUT_MS = 28_000;
export const VALIDATE_TIMEOUT_MS = 25_000;

/** POST /api/projects/migrate response (specs/api/projects.yaml MigrateProjectResponse). */
export interface MigrateProjectResult {
  project: { jira_key: string; title: string; current_phase: string } & Record<
    string,
    unknown
  >;
  linked_tools: MigrationTool[];
  governing_events_backfilled: number[];
  team_assigned: number;
  /**
   * SharePoint SOW link with no uploaded file: `queued` — the server downloads it into the
   * project's SOW documents; `failed` — it could not be queued; null — nothing to download.
   */
  sow_download?: "queued" | "failed" | null;
  sow_upload: {
    upload_url: string;
    s3_key: string;
    content_type: string;
    expires_in: number;
  } | null;
  propagation: { attempted: boolean; completed: boolean };
}

export function useMigrateProject() {
  const client = useApiClient();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: MigrateProjectPayload) => {
      const response = await client.post<MigrateProjectResult>(
        "/api/projects/migrate",
        payload,
        // Errors are rendered inline by the wizard (field-scoped), not as a global toast.
        { timeout: MIGRATE_TIMEOUT_MS, _suppressErrorToast: true },
      );
      return response.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["projects"] });
    },
  });
}

/** Imperative per-field check for the wizard's Validate buttons. Throws on a request failure. */
export function useValidateMigrationIntegration() {
  const client = useApiClient();
  return async (
    tool: MigrationTool,
    value: string,
  ): Promise<IntegrationCheckResult> => {
    const response = await client.post<IntegrationCheckResult>(
      "/api/projects/migrate/validate",
      { tool, value },
      { timeout: VALIDATE_TIMEOUT_MS, _suppressErrorToast: true },
    );
    return response.data;
  };
}
